import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/functions';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@app/core/auth/auth.service';
import { ApplicationLoadingService } from '@app/core/services/application-loading.service';
import { StripeService } from '@app/core/services/stripe.service';
import { TermsOfAgreementComponent } from '@app/shared/components/terms-of-agreement/terms-of-agreement.component';
import { DateUtil } from '@app/shared/utils/date-util.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NotifierService } from 'angular-notifier';
import { BehaviorSubject, forkJoin, merge, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { IBankAccount } from 'stripe';

enum TaskIdentifier {
  UPDATE_ACCOUNT = 'UPDATE_ACCOUNT',
  ADD_BANK = 'ADD_BANK'
}

@Component({
  selector: 'app-stripe-payout-settings',
  templateUrl: './stripe-payout-settings.component.html',
  styleUrls: ['./stripe-payout-settings.component.scss']
})
export class StripePayoutSettingsComponent implements OnInit, OnDestroy {

  accountDetailsForm = new FormGroup({
    email: new FormControl('', Validators.compose([Validators.required, Validators.email])),
    gender: new FormControl('male', Validators.compose([Validators.required])),
    bDay: new FormControl('', Validators.compose([Validators.required])),
    address: new FormControl('', Validators.compose([Validators.required])),
    lname: new FormControl('', Validators.compose([Validators.required])),
    frontId: new FormControl(''), // Validators.compose([Validators.required])),
    fname: new FormControl('', Validators.compose([Validators.required])),
    // postal_code: new FormControl('', Validators.compose([Validators.required])),
    p_id: new FormControl('', Validators.compose([Validators.required])), // 123131185
    phoneNum: new FormControl(
      '',
      Validators.compose([
        Validators.required,
        Validators.pattern(/\65(6|8|9)\d{7}/g)
      ])
    ),
  });
  idProvided = false;

  dateValue: Date;

  bankDetailsForm = new FormGroup({
    name: new FormControl('', Validators.compose([Validators.required])),
    accountNo: new FormControl('', Validators.compose([Validators.required])),
    branchcode: new FormControl('', Validators.compose(
      [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(3),
        Validators.pattern('[0-9]*')
      ])),
    bankcode: new FormControl('', Validators.compose(
      [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(4),
        Validators.pattern('[0-9]*')
      ]))
  });

  disableSave = false;
  stripeAccountId = '';

  @Input() modalRef: NgbModalRef;

  temporaryBanks = [];

  stripeAccountData$ = new BehaviorSubject(null);
  stripeAccountDataLoading$ = new BehaviorSubject(false);
  bankAccounts$: Observable<IBankAccount[]>;
  bankAccountsLoading$ = new BehaviorSubject(false);

  unsubscribe$ = new BehaviorSubject(false);

  oldAccountDetailsForm: any;

  allowDismiss = true;
  allowDismiss$ = new Promise(res => res(false));

  formsSubscriber: Subscription;

  constructor(
    private authService: AuthService,
    private functions: AngularFireFunctions,
    private dateUtil: DateUtil,
    private applicationLoadingService: ApplicationLoadingService,
    private stripeService: StripeService,
    private notifierService: NotifierService,
    private ngbModal: NgbModal
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();

    this.formsSubscriber.unsubscribe();
    this.formsSubscriber = null;
  }

  ngOnInit(): void {
    this.loadStripeUserAccount();
    this.listenToForms();
  }

  loadStripeUserAccount() {
    this.authService.user
      .pipe(
        debounceTime(300),
        switchMap(user => {
          this.applicationLoadingService.toggleLoading(true);
          this.stripeAccountId = user.stripe_account_ID;
          return !!user && user.stripe_account_ID
            ? this.functions.httpsCallable('getStripeAccount')({
              accountId: user.stripe_account_ID
            })
            : of(null)
        }),
        withLatestFrom(this.authService.user),
        tap(([stripeAccountData, user]) => {
          this.stripeAccountData$.next(stripeAccountData);
          if (!!stripeAccountData && stripeAccountData.individual) {
            let { dob } = stripeAccountData.individual;
            this.dateValue = this.dateUtil.parse(
              `${dob.year}-${dob.month}-${dob.day}`,
              'YYYY-M-D'
            );

            const values = {
              email: stripeAccountData.individual.email,
              gender: stripeAccountData.individual.gender,
              phoneNum: stripeAccountData.individual.phone,

              fname: stripeAccountData.individual.first_name,
              lname: stripeAccountData.individual.last_name,
              bDay: this.convertDateToObject(this.dateValue),

              address: stripeAccountData.individual.address.line1,
              postal_code: stripeAccountData.individual.address.postal_code,
              // frontId: stripeAccountData.individual.address.email, // Validators.compose([Validators.required])),
            };

            this.accountDetailsForm.patchValue({
              ...values
            });

            this.oldAccountDetailsForm = { ...values };

            if (stripeAccountData.individual.id_number_provided) {
              this.accountDetailsForm.controls.p_id.disable();
              this.accountDetailsForm.controls.p_id.patchValue('********')
              this.idProvided = true;
            }

            this.accountDetailsForm.updateValueAndValidity();
          } else {
            const values = {
              email: user.email,
              phoneNum: user.mobile,

              fname: user.firstName,
              lname: user.lastName,

              address: user.address,
              // frontId: stripeAccountData.individual.address.email, // Validators.compose([Validators.required])),
            };

            if (user.birthDay) {
              this.dateOfBirthDatePickerChange(
                this.convertObjectToDate(user.birthDay)
              );
            }

            this.accountDetailsForm.patchValue({
              ...values
            });
          }

          this.applicationLoadingService.toggleLoading(false);

          this.bankAccounts$ = of(
            stripeAccountData?.external_accounts?.data
            || []
          );
        }),
        take(1)
      ).subscribe();
  }

  listenToForms() {
    //#region debugging
    // this.accountDetailsForm.valueChanges.subscribe(dat => {
    //   console.log(this.accountDetailsForm.dirty
    //     && !this.existingAccountDetailsValuesIdentical(this.accountDetailsForm.value));
    // });
    //#endregion

    this.formsSubscriber = merge(
      this.accountDetailsForm.valueChanges,
      this.bankDetailsForm.valueChanges
    )
      // .pipe(takeUntil(this.unsubscribe$))
      .subscribe(_ => {
        // console.log('dat', dat);
        this.evaluateModalDismissable();
      });
  }

  dateOfBirthDatePickerChange(value) {
    this.dateValue = value;
    this.accountDetailsForm.controls.bDay.patchValue(
      this.convertDateToObject(this.dateValue)
    );
    this.accountDetailsForm.updateValueAndValidity();
  }

  convertDateToObject(date: Date) {
    return !!date ? {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate()
    } : null;
  }

  convertObjectToDate(dateObject) {
    if (
      !!dateObject
      && dateObject.year
      && dateObject.day
      && dateObject.month
    ) {
      const result = new Date();
      result.setFullYear(dateObject.year);
      result.setDate(dateObject.day);
      result.setHours(0);
      result.setMilliseconds(0);
      result.setMinutes(0);
      result.setMonth(dateObject.month);
      return result;
    } else {
      return new Date();
    }
  }

  save() {
    // prep details form (date of birth to object)
    // check added bank validity
    // check terms
    // remove pid on send
    if (
      this.accountDetailsForm.valid
      && (this.bankDetailsForm.valid || !this.bankDetailsForm.dirty)
    ) {
      if (this.stripeAccountId) {
        const tasks = [];
        // TODO add disable save
        if (!this.existingAccountDetailsValuesIdentical(this.accountDetailsForm.value)) {
          // console.log('this.accountDetailsForm', this.accountDetailsForm.value);
          // save accout details
          tasks.push(
            this.stripeService.updateStripePayoutAccountDetails(
              this.stripeAccountId,
              this.accountDetailsForm.value,
              this.idProvided
            )
              .pipe(
                map(res => ({
                  identifier: TaskIdentifier.UPDATE_ACCOUNT,
                  ...res
                }))
              )
          );
        }

        if (this.bankDetailsForm.dirty && this.bankDetailsForm.valid) {
          tasks.push(
            this.stripeService.addExternalBankAccount(
              this.convertBankFormToStripeBankAccountForm(
                this.bankDetailsForm.value
              )
            )
              .pipe(
                map(res => ({
                  identifier: TaskIdentifier.ADD_BANK,
                  ...res
                }))
              )
          );
        }

        // exexute tasks
        if (tasks && tasks.length) {
          this.executeSaveTasks(tasks);
        }
      } else {
        this.disableSave = true;
        this.applicationLoadingService.toggleLoading(true);

        const addBank = this.bankDetailsForm.valid && this.bankDetailsForm.dirty;
        this.stripeService.updateStripePayoutAccountDetails(
          this.stripeAccountId,
          this.accountDetailsForm.value,
          this.idProvided
        ).pipe(
          take(2),
          catchError(() => {
            this.notifierService.notify(
              'error',
              `An error occured while ${this.stripeAccountId ? 'updating' : 'creating'} account`
            );
            return of(null);
          }),
          switchMap(accountResponse => {
            return accountResponse
              && accountResponse.data
              && !accountResponse.error
              && addBank
              ? this.stripeService.addExternalBankAccount(
                this.convertBankFormToStripeBankAccountForm(
                  this.bankDetailsForm.value
                )
              )
                .pipe(
                  take(1),
                  catchError(() => {
                    this.notifierService.notify(
                      'error',
                      `An error occured while adding bank account`
                    );
                    return of(null);
                  }),
                )
              : of(addBank ? null : accountResponse);
          })
        )
          .subscribe(result => {
            if (result) {
              const bankAccountMessage = addBank ? ' and bank ' : '';
              this.notifierService.notify(
                'success',
                `Successfully created user ${bankAccountMessage}account`
              );

              this.loadStripeUserAccount();
              this.evaluateModalDismissable();
              this.disableSave = false;
              this.applicationLoadingService.toggleLoading(false);
            }
          });
      }
    } else {
      if (
        this.accountDetailsForm.dirty
        && !this.accountDetailsForm.valid
      ) {
        this.notifierService.notify(
          'warning',
          this.getFormError(this.accountDetailsForm)
        );
      }

      if (
        this.bankDetailsForm.dirty
        && !this.bankDetailsForm.valid
      ) {
        this.notifierService.notify(
          'warning',
          this.getFormError(this.bankDetailsForm)
        );
      }
    }
  }

  getFormError(fg: FormGroup) {
    for (const key of Object.keys(fg.controls)) {
      if (fg.controls[key].errors) {
        const firstError = Object.keys(fg.controls[key].errors)[0];
        return `${this.getFormControlLabelMapping(key)} ${this.getFormControlValidityLabel(firstError)}.`;
      }
    }
    return;
  }

  private getFormControlLabelMapping(label: string): string {
    if (!label) {
      return '';
    }

    switch (label) {
      case 'bDay':
        return 'Date of Birth';
      case 'p_id':
        return 'Id Number';
      case 'phoneNum':
        return 'Phone number';
      case 'lname':
        return 'Last name';
      case 'fname':
        return 'First name';
      case 'accountNo':
        return 'Account number';
      case 'branchcode':
        return 'Branch code';
      case 'bankcode':
        return 'Bank code';
      default:
        return label.charAt(0).toUpperCase()
          + label.slice(1).toLowerCase();
    }
  }

  private getFormControlValidityLabel(validity: string): string {
    switch (validity) {
      case 'required':
        return 'is required';
      case 'min':
        return 'is required';
      case 'minlength':
        return 'is too short';
      case 'maxlength':
        return `is too long`;
      case 'pattern':
        return `is invalid`;
      default:
        return validity;
    }
  }

  executeSaveTasks(tasks: Observable<any>[]) {
    this.disableSave = true;
    this.applicationLoadingService.toggleLoading(true);

    forkJoin(tasks)
      .pipe(
        take(1),
      )
      .subscribe((results) => {
        console.log(results);

        let notificationType = 'success';
        let notificationMessage = '';
        let warnings = 0;

        results.forEach((result: any) => {
          if (!!result && result.identifier) {
            if (notificationMessage.length && result.length > 1) {
              notificationMessage = notificationMessage.concat(' and ');
            }
            switch (result.identifier) {
              case TaskIdentifier.ADD_BANK:
                notificationMessage = notificationMessage.concat(
                  !result.error
                    ? 'Successfully added bank account'
                    : result.error
                );
                break;
              case TaskIdentifier.UPDATE_ACCOUNT:
                notificationMessage = notificationMessage.concat(
                  !result.error
                    ? 'Successfully updated user details'
                    : result.error
                );
                break;
            }

            if (!!result.error) {
              warnings++;
            }
          }
        });

        if (warnings > (results.length / 2)) {
          notificationType = 'error';
        } else if (warnings) {
          notificationType = 'warning';
        }

        notificationMessage = `${notificationMessage}!`;

        this.loadStripeUserAccount();
        this.evaluateModalDismissable();
        this.disableSave = false;
        this.applicationLoadingService.toggleLoading(false);

        this.notifierService.notify(
          notificationType,
          notificationMessage
        );
      });
  }

  evaluateModalDismissable() {
    this.allowDismiss = (
      !this.accountDetailsForm.dirty
      || this.existingAccountDetailsValuesIdentical(this.accountDetailsForm.value)
    )
      && (
        !this.bankDetailsForm.dirty
        && !this.bankDetailsForm.touched
      );
    this.allowDismiss$ = new Promise(res => res(this.allowDismiss));
  }

  existingAccountDetailsValuesIdentical(values) {
    // same all except id
    return !!this.oldAccountDetailsForm && !!values
      ? Object.keys(this.oldAccountDetailsForm)
        .filter(key => key !== 'postal_code')
        .map(key =>
          !!this.oldAccountDetailsForm[key]
          && !!values[key]
          && this.oldAccountDetailsForm[key] === values[key]
        )
        .reduce((a, b) => a && b, true)
      : false;
  }

  setBankAccountAsCurrencyDefault(id: string) {
    this.applicationLoadingService.toggleLoading(true);

    this.stripeService.setBankAccountAsDefaultForCurrency(
      id
    )
      .pipe(take(1))
      .subscribe(res => {
        this.notifierService.notify(
          'success',
          'Successfuly updated default account.'
        );
        this.loadStripeUserAccount();
      }, err => {
        this.notifierService.notify(
          'error',
          JSON.stringify(err)
        );
      }, () => {
        this.applicationLoadingService.toggleLoading(true);
      });
    // see changes on user form?
  }

  removeBankAccount(id: string) {
    this.stripeService.deleteExternalBankAccount(
      id
    )
      .pipe(take(1))
      .subscribe(res => {
        this.notifierService.notify(
          'success',
          'Successfuly deleted bank account.'
        );
        this.loadStripeUserAccount();
      }, err => {
        this.notifierService.notify(
          'error',
          JSON.stringify(err)
        );
      }, () => {
        this.applicationLoadingService.toggleLoading(true);
      });
  }

  convertBankFormToStripeBankAccountForm(values: any) {
    return {
      country: 'SG',
      currency: 'SGD',
      routing_number: values.bankcode + '-' + values.branchcode, // 1100-000
      account_number: values.accountNo,
      account_holder_name: values.name,
      account_holder_type: 'individual',
      p_id: values.p_id
    }
  }

  showTermsButton(stripeAccountData) {
    return this.stripeService.accountHasNotAcceptedTerms(stripeAccountData);
  }

  showTermsModal() {
    this.authService.user
      .pipe(
        take(1),
        withLatestFrom(this.stripeAccountData$)
      )
      .subscribe(([user, stripeAccoutData]) => {
        if (
          !!user
          && !!user.email
          && this.stripeAccountId
          && !stripeAccoutData?.tos_acceptance?.date
        ) {
          const stripeAccountModal = this.ngbModal.open(TermsOfAgreementComponent);
          stripeAccountModal.componentInstance.email = user.email;
          stripeAccountModal.componentInstance.stripe_account_ID = this.stripeAccountId;

          stripeAccountModal.componentInstance
            .result
            .subscribe(event => {
              if (event === 'Terms of Agreement Success') {
                this.notifierService.notify('sucess', 'Successfully accepted terms');
              } else {
                this.notifierService.notify('error', 'Unable to accept terms. Please contact support@rentreef.com');
              }
              this.ngbModal.dismissAll();
            })
        }
      });
  }

}
